/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext } from 'react';
import { useDrag } from 'react-dnd';
import { Flex } from 'antd';

import TaskContext from '@totem/components/ticketing/details/tasks/taskContext';
import TaskSpecificButton from '@totem/components/ticketing/details/tasks/taskSpecific/taskSpecificButton';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { ticketPermissions } from '@totem/components/ticketing/permissions';
import { Task, TASK_TYPE_TASK } from '@totem/components/ticketing/ticketing';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import IBDIcon from '@totem/styles/Icon';
import { DateStringToLocal } from '@totem/utilities/dateUtilities';

const showResourcesForStatus = ['PENDING', 'IN PROGRESS', 'SCHEDULED'];

type Props = {
  color: string;
  task: Task;
};

const TaskCard = ({ color, task }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { ticket } = useContext(TicketContext);
  const { tasks } = useContext(TaskContext);
  const [{ isDragging }, drag] = useDrag(() => ({
    type: TASK_TYPE_TASK,
    item: task,
    canDrag:
      task.type === TASK_TYPE_TASK &&
      ticketPermissions.taskStatusChanger.hasPermissions(
        userProfile,
        ticket,
        task,
        null,
      ),
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  const getResourceNames = () => {
    const resourceNames = [];

    task.assignedResources.forEach((assignedResource) => {
      ticket.ticket.partiesInvolved.forEach((pi) => {
        if (pi.id === assignedResource) {
          if (!resourceNames.includes(pi.name)) {
            resourceNames.push(pi.name);
          }
        }
      });
    });

    return resourceNames;
  };

  const getResourcesDisplay = () => {
    const resourceNames = getResourceNames();
    if (resourceNames.length > 0) {
      if (resourceNames.length === 1) {
        return (
          <div style={{ paddingLeft: '32px' }}>
            Resource: {resourceNames[0]}
          </div>
        );
      }
      return (
        <>
          <div style={{ paddingLeft: '32px' }}>Resources:</div>
          {resourceNames.map((resourceName) => (
            <div key={resourceName} style={{ paddingLeft: '40px' }}>
              {resourceName}:
            </div>
          ))}
        </>
      );
    }
    return <span />;
  };

  const getDependencies = () => {
    const dependencyNumbers = task.dependencies.map((dep) => dep.taskId);
    const dependencies = tasks.filter(
      (chk) =>
        dependencyNumbers.includes(chk.number) &&
        chk.status !== 'COMPLETED' &&
        chk.status !== 'CANCELLED',
    );
    return (
      <>
        {dependencies.map((dep) => (
          <div key={dep.id}>
            {dep.number}: {dep.status}
          </div>
        ))}
      </>
    );
  };

  return (
    <div
      ref={drag}
      style={{
        borderStyle: 'solid',
        borderWidth: 2,
        borderColor: color,
        marginBottom: 8,
        padding: '4px',
      }}
    >
      <div style={{ fontWeight: 700 }}>
        <Flex justify={'space-between'} align={'flex-start'}>
          <Flex justify={'flex-start'} align={'flex-start'}>
            <IBDIcon
              name={task.type}
              style={{
                marginRight: '16px',
                marginTop: '4px',
                color: task.internalOnly
                  ? colors.event.criticality.background.critical
                  : colors.event.criticality.background.ok,
              }}
            />
            {task.number}: {task.name}
          </Flex>
          <div>
            <TaskSpecificButton task={task} />
          </div>
        </Flex>
      </div>
      {task.status === 'SCHEDULED' && (
        <div style={{ paddingLeft: '32px' }}>
          Scheduled Start: {DateStringToLocal(task.expectedStartDate)}
        </div>
      )}
      {task.status === 'IN PROGRESS' && (
        <div style={{ paddingLeft: '32px' }}>
          Started: {DateStringToLocal(task.actualStartDate)}
        </div>
      )}
      {task.status === 'COMPLETED' && (
        <div style={{ paddingLeft: '32px' }}>
          Completed: {DateStringToLocal(task.actualEndDate)}
        </div>
      )}
      {showResourcesForStatus.includes(task.status) && getResourcesDisplay()}
      {task.status === 'BLOCKED' && (
        <div style={{ paddingLeft: '32px' }}>
          Dependencies:
          <div style={{ paddingLeft: '16px' }}>{getDependencies()}</div>
        </div>
      )}
    </div>
  );
};

export default TaskCard;
